import type { IconType } from "../../components/Icon.types";
import { MESSAGE_CUSTOM_TYPES, type MessageCustomType } from "../constants";
import { parseCustomMessage } from "./parseCustomMessage";

/**
 * Color context for system event messages.
 * Maps to theme palette colors for contextual styling.
 */
export type SystemEventColorContext = "success" | "error" | "info" | "neutral" | "muted";

/**
 * Style configuration for a system event message type.
 * Display text always comes from the backend message payload;
 * this config only controls icon and color styling.
 */
interface SystemEventStyleConfig {
  /** Color context for themed styling */
  colorContext: SystemEventColorContext;
  /** Icon to display in the card */
  iconType: IconType;
}

/**
 * Style configurations for system event message types.
 * Maps custom type strings to icon and color context.
 * Display text is always extracted from the message payload (facility field).
 */
const SYSTEM_EVENT_STYLE_CONFIGS: Partial<Record<MessageCustomType, SystemEventStyleConfig>> = {
  [MESSAGE_CUSTOM_TYPES.SHIFT_INVITE_REQUEST]: {
    colorContext: "neutral",
    iconType: "message",
  },
  [MESSAGE_CUSTOM_TYPES.SHIFT_INVITE_REMINDER]: {
    colorContext: "neutral",
    iconType: "message",
  },
  [MESSAGE_CUSTOM_TYPES.SHIFT_TIME_PROPOSAL_CREATED]: {
    colorContext: "neutral",
    iconType: "clock",
  },
  [MESSAGE_CUSTOM_TYPES.WORKER_ACCEPT_SHIFT_INVITE]: {
    colorContext: "success",
    iconType: "check",
  },
  [MESSAGE_CUSTOM_TYPES.WORKER_REJECT_SHIFT_INVITE]: {
    colorContext: "muted",
    iconType: "close",
  },
  [MESSAGE_CUSTOM_TYPES.WORKER_MARK_SHIFT_INTEREST]: {
    colorContext: "info",
    iconType: "badge-check",
  },
  [MESSAGE_CUSTOM_TYPES.WORKER_UPDATED_ETA]: {
    colorContext: "info",
    iconType: "clock",
  },
  [MESSAGE_CUSTOM_TYPES.WORKER_CHECKED_IN]: {
    colorContext: "success",
    iconType: "check",
  },
  [MESSAGE_CUSTOM_TYPES.WORKER_CHECKED_OUT]: {
    colorContext: "neutral",
    iconType: "check",
  },
  [MESSAGE_CUSTOM_TYPES.SHIFT_BOOKED]: {
    colorContext: "success",
    iconType: "check",
  },
  [MESSAGE_CUSTOM_TYPES.SHIFT_CANCELLED]: {
    colorContext: "error",
    iconType: "close",
  },
  [MESSAGE_CUSTOM_TYPES.SHIFT_UNASSIGNED]: {
    colorContext: "error",
    iconType: "close",
  },
};

/**
 * Default icon type for unknown system event messages.
 */
const DEFAULT_ICON_TYPE: IconType = "information";

/**
 * Result of getting system event display info
 */
export interface SystemEventDisplay {
  displayText: string;
  colorContext: SystemEventColorContext;
  iconType: IconType;
}

/**
 * Get display text, color context, and icon for a system event message.
 * Display text always comes from the message payload (facility field in JSON),
 * matching the legacy chat behavior. The config only provides icon and color styling.
 *
 * @param customType - The Sendbird custom type string
 * @param messageContent - The raw message content (may be JSON)
 */
export function getSystemEventDisplay(
  customType: string,
  messageContent: string
): SystemEventDisplay {
  const displayText = parseCustomMessage(messageContent);
  const config = SYSTEM_EVENT_STYLE_CONFIGS[customType as MessageCustomType];

  return {
    displayText,
    colorContext: config?.colorContext ?? "neutral",
    iconType: config?.iconType ?? DEFAULT_ICON_TYPE,
  };
}

/**
 * Check if a custom type has a specific style configuration
 */
export function hasSystemEventDisplayConfig(customType: string): boolean {
  return customType in SYSTEM_EVENT_STYLE_CONFIGS;
}
